export class AudioAnalyzer {
  private audioCtx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private animFrameId: number | null = null;
  private isSpeaking = false;
  private speakingCounter = 0;
  private quietCounter = 0;

  public onVolume?: (volume: number) => void;
  public onSpeakingChange?: (isSpeaking: boolean) => void;

  public attachStream(stream: MediaStream) {
    this.stop();

    const audioTracks = stream.getAudioTracks();
    if (audioTracks.length === 0) return;

    try {
      const AudioCtxClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.audioCtx = new AudioCtxClass();
      this.analyser = this.audioCtx.createAnalyser();
      this.analyser.fftSize = 256;
      this.analyser.smoothingTimeConstant = 0.5;

      this.source = this.audioCtx.createMediaStreamSource(stream);
      this.source.connect(this.analyser);

      this.startLoop();
    } catch (err) {
      console.warn('AudioContext initialization bypassed:', err);
    }
  }

  private startLoop() {
    if (!this.analyser) return;

    const dataArray = new Uint8Array(this.analyser.frequencyBinCount);

    const checkAudio = () => {
      if (!this.analyser) return;
      this.analyser.getByteFrequencyData(dataArray);

      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        sum += dataArray[i];
      }
      const avg = sum / dataArray.length;
      const normalized = Math.min(1.0, avg / 128.0);

      if (this.onVolume) {
        this.onVolume(normalized);
      }

      // Google-Meet-grade hysteresis: speech must sustain ~250ms to switch the
      // highlight on, and ~600ms of silence to switch it off. Prevents the
      // speaker highlight (and active_speaker broadcasts) from strobing on
      // background noise.
      const speakingThreshold = 0.08;
      const framesToStartSpeaking = 15;
      const quietFramesToStopSpeaking = 36;

      if (normalized > speakingThreshold) {
        this.speakingCounter++;
        this.quietCounter = 0;
        if (!this.isSpeaking && this.speakingCounter >= framesToStartSpeaking) {
          this.isSpeaking = true;
          if (this.onSpeakingChange) this.onSpeakingChange(true);
        }
      } else {
        this.speakingCounter = Math.max(0, this.speakingCounter - 2);
        if (this.isSpeaking) {
          this.quietCounter++;
          if (this.quietCounter >= quietFramesToStopSpeaking) {
            this.isSpeaking = false;
            this.speakingCounter = 0;
            this.quietCounter = 0;
            if (this.onSpeakingChange) this.onSpeakingChange(false);
          }
        }
      }

      this.animFrameId = requestAnimationFrame(checkAudio);
    };

    checkAudio();
  }

  public stop() {
    if (this.animFrameId) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = null;
    }
    if (this.source) {
      this.source.disconnect();
      this.source = null;
    }
    if (this.analyser) {
      this.analyser.disconnect();
      this.analyser = null;
    }
    if (this.audioCtx && this.audioCtx.state !== 'closed') {
      void this.audioCtx.close().catch(() => {});
      this.audioCtx = null;
    }
    this.isSpeaking = false;
    this.speakingCounter = 0;
    this.quietCounter = 0;
  }
}
